import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { FileSearch, Loader2, Clock, Play } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface RunExtractButtonProps {
  slug: string;
  disabled?: boolean;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  selectedVersion?: string | null;
}

type TaskStatus = 'idle' | 'pending' | 'running';

export function RunExtractButton({
  slug,
  disabled = false,
  variant = 'outline',
  size = 'sm',
  className,
  selectedVersion = null,
}: RunExtractButtonProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [taskStatus, setTaskStatus] = useState<TaskStatus>('idle');
  const [showVersionModal, setShowVersionModal] = useState(false);
  const { toast } = useToast();

  // Check for existing pending/running tasks
  useEffect(() => {
    if (!slug) return;

    const checkExistingTask = async () => {
      const { data } = await supabase
        .from('claude_tasks')
        .select('status')
        .eq('slug', slug)
        .eq('command', 'run_extract')
        .in('status', ['pending', 'running'])
        .order('created_at', { ascending: false })
        .limit(1);

      if (data && data.length > 0) {
        setTaskStatus(data[0].status as TaskStatus);
      } else {
        setTaskStatus('idle');
      }
    };

    checkExistingTask();

    // Subscribe to realtime updates
    const channel = supabase
      .channel(`extract-task-${slug}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'claude_tasks',
          filter: `slug=eq.${slug}`,
        },
        () => {
          checkExistingTask();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [slug]);

  const handleButtonClick = () => {
    // If a version is selected, show the modal
    if (selectedVersion) {
      setShowVersionModal(true);
    } else {
      // Otherwise, run extract directly
      runExtract();
    }
  };

  const runExtract = async (version?: string) => {
    if (!slug || isSubmitting || taskStatus !== 'idle') return;

    setIsSubmitting(true);
    setShowVersionModal(false);

    try {
      // Get latest successful scrape version to use as period
      let period: string | null = null;

      const { data: scrapes, error: scrapeError } = await supabase
        .from('scrapes')
        .select('version, verified')
        .eq('slug', slug)
        .eq('verified', true)
        .order('version', { ascending: false })
        .limit(1);

      if (scrapeError) {
        console.error('Error fetching latest scrape:', scrapeError);
      } else if (scrapes && scrapes.length > 0) {
        period = scrapes[0].version;
      }

      const params: { slug: string; period?: string; version?: string; force?: boolean } = {
        slug
      };

      // Add period if we found a latest successful scrape
      if (period) {
        params.period = period;
      }

      if (version) {
        params.version = version;
        params.force = true;
      }

      const { error } = await supabase
        .from('claude_tasks')
        .insert({
          command: 'run_extract',
          slug: slug,
          params,
          status: 'pending',
          priority: 5,
        });

      if (error) {
        throw error;
      }

      setTaskStatus('pending');

      const description = version
        ? `Re-running extraction for "${slug}" version ${version}`
        : `Extraction queued for "${slug}"${period ? ` (period: ${period})` : ''}`;

      toast({
        title: 'Extract task created',
        description,
      });
    } catch (error) {
      console.error('Error creating extract task:', error);
      toast({
        title: 'Error creating task',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRerunSelected = () => {
    if (selectedVersion) {
      runExtract(selectedVersion);
    }
  };

  const handleRunLatest = () => {
    runExtract();
  };

  const isDisabled = disabled || isSubmitting || !slug || taskStatus !== 'idle';

  // Determine button content based on state
  const getButtonContent = () => {
    if (isSubmitting) {
      return (
        <>
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Creating...
        </>
      );
    }
    if (taskStatus === 'running') {
      return (
        <>
          <Play className="h-4 w-4 mr-2 text-green-600" />
          Running...
        </>
      );
    }
    if (taskStatus === 'pending') {
      return (
        <>
          <Clock className="h-4 w-4 mr-2 text-amber-600" />
          Pending
        </>
      );
    }
    return (
      <>
        <FileSearch className="h-4 w-4 mr-2" />
        Run Extract
      </>
    );
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleButtonClick}
        disabled={isDisabled}
        className={className}
      >
        {getButtonContent()}
      </Button>

      <AlertDialog open={showVersionModal} onOpenChange={setShowVersionModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Run Extract</AlertDialogTitle>
            <AlertDialogDescription>
              You have version <strong>{selectedVersion}</strong> selected. Would you like to:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={handleRerunSelected}
            >
              Re-run Selected Version
            </Button>
            <AlertDialogAction onClick={handleRunLatest}>
              Run on Latest
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
